import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { ORG_WIDE_REPORT_IDS } from '@/lib/roles';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Users,
  Ticket,
  Clock,
  CheckCircle,
  AlertTriangle,
  RefreshCw,
  Activity,
  Star,
  Bot,
  MessageCircle,
  Zap,
  Shield,
  GitBranch,
  Gauge,
  ExternalLink,
  Archive,
  FileClock,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { useTicketsListStore } from '../../stores/ticketsListStore';
import { NLQueryBar } from '../dashboard/widgets/NLQueryBar';
import { statusLabelToFilterValue } from './statusFilter';

// 26c — every surface on Reports is a card floating on the canvas.
//
// Both values are theme TOKENS rather than the artboard's literal hexes: the
// palette already resolves `--co-raised`/`--co-line` to #FFFFFF/#DFE3E9 and
// `--co-shadow-card` to the exact two-layer shadow 26c draws, so the same
// markup is correct in dark mode and under the other theme.
const CARD_CLASS = 'rounded-[10px] border border-[var(--co-line)] bg-[var(--co-raised)]';
const CARD_STYLE: React.CSSProperties = { boxShadow: 'var(--co-shadow-card)' };

// Role-based scope helpers
const ADMIN_ROLES = ['admin', 'owner'];
const SUPERVISOR_ROLES = ['supervisor'];

/**
 * A one-line, sideways-scrolling row that shows it has more: an edge fade and
 * an arrow button on each side that still has content. Without them the row
 * simply ended in a cut-off tab with no sign there was anything beyond.
 */
function ScrollCue({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setEdges({
      left: el.scrollLeft > 2,
      right: el.scrollLeft + el.clientWidth < el.scrollWidth - 2,
    });
  }, []);

  useEffect(() => {
    measure();
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure, children]);

  const nudge = (dir: 1 | -1) =>
    ref.current?.scrollBy({ left: dir * Math.max(160, (ref.current?.clientWidth || 0) * 0.6), behavior: 'smooth' });

  const arrow =
    'absolute top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full border border-[var(--co-line-strong)] bg-[var(--co-raised)] text-[var(--co-ink-2)] shadow-sm hover:text-[var(--co-ink)]';

  return (
    <div className="relative">
      <div
        ref={ref}
        onScroll={measure}
        className="co-scrollbar-none flex flex-nowrap items-center gap-1 overflow-x-auto pb-1"
      >
        {children}
      </div>
      {edges.left && (
        <>
          <div className="pointer-events-none absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-[var(--co-raised)] to-transparent" />
          <button type="button" aria-label="Show earlier reports" onClick={() => nudge(-1)} className={`${arrow} left-0`}>
            <ChevronLeft className="h-4 w-4" />
          </button>
        </>
      )}
      {edges.right && (
        <>
          <div className="pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-[var(--co-raised)] to-transparent" />
          <button type="button" aria-label="Show more reports" onClick={() => nudge(1)} className={`${arrow} right-0`}>
            <ChevronRight className="h-4 w-4" />
          </button>
        </>
      )}
    </div>
  );
}

function getUserScope(role: string): 'all' | 'team' | 'self' {
  if (ADMIN_ROLES.includes(role)) return 'all';
  if (SUPERVISOR_ROLES.includes(role)) return 'team';
  return 'self';
}
import { Badge } from '@centraoneplus/ui';
import { ExportButton } from '@components/common/ExportButton';
import { SingleDatePicker } from '../../components/common/SingleDatePicker';
import { TeamFilterPicker } from '../../components/teams/TeamFilterPicker';
import { TeamPerformanceTable, orderTeamRows } from './TeamPerformanceTable';
import { makePieLabelRenderer } from '../../components/charts/pieLabel';
import { useThemeColors } from '../../components/charts/useThemeColors';
import GeneratedReportsModal from './GeneratedReportsModal';
import workflowService from '../../services/workflow.service';
import { ExportColumn, ExportTable, formatters } from '@utils/export';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';
import {
  useDashboardMetrics,
  useTeamPerformance,
  useAgentMetrics,
  useSlaMetrics,
  useTicketTrends,
  useCsatStats,
  useChatStats,
  useAutomationRuleStats,
  useAutomationExecutionStats,
  useAiChatbotAnalytics,
  usePipelines,
  analyticsKeys,
} from '../../hooks/useAnalytics';
import { useCustomMetricsList, useEvaluateMetric, useMetricTimeSeries } from '../../hooks/useDashboards';
import { analyticsService, DateRange } from '../../services/analytics.service';
import { ticketService } from '../../services/ticket.service';
import { teamMembersService } from '../../services/team-members.service';
import type { Ticket as TicketEntity } from '@centraoneplus/shared-types';
import { useQueryClient } from '@tanstack/react-query';
import { PageHeaderMeta } from '@components/layouts/PageHeaderMeta';

// Date range helpers — the report window is selected via an explicit start/end
// date-range picker. Dates are formatted as YYYY-MM-DD, the format the analytics
// endpoints expect as startDate/endDate query params.
function toDateInput(d: Date): string {
  return d.toISOString().split('T')[0];
}

// Default window: from January 1st of the current year through today.
function getDefaultDateRange(): DateRange {
  const end = new Date();
  // Build Jan-1 directly from the local year to avoid a UTC roll-back to the
  // prior year's Dec 31 in timezones ahead of UTC (toDateInput uses toISOString).
  const start = `${end.getFullYear()}-01-01`;
  return { start, end: toDateInput(end) };
}

// Report types
const REPORT_TYPES = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'backtracked', label: 'Backtracked', icon: Archive },
  { id: 'tickets', label: 'Ticket Analytics', icon: Ticket },
  { id: 'agents', label: 'Agent Performance', icon: Users },
  { id: 'teams', label: 'Team Performance', icon: Users },
  { id: 'sla', label: 'SLA Compliance', icon: Clock },
  { id: 'channels', label: 'Channel Analysis', icon: Activity },
  { id: 'csat', label: 'CSAT / NPS', icon: Star },
  { id: 'ai', label: 'AI Chatbot', icon: Bot },
  { id: 'chat', label: 'Live Chat', icon: MessageCircle },
  { id: 'automations', label: 'Automations', icon: Zap },
  { id: 'pipelines', label: 'Pipeline Analysis', icon: GitBranch },
  { id: 'custom-metrics', label: 'Custom Metrics', icon: Gauge },
];

// Each active custom metric gets its OWN Reports tab (id = this prefix + the
// metric id). The 'custom-metrics' tab remains as an overview / empty-state.
const CUSTOM_METRIC_PREFIX = 'custom-metric:';

// Title-case for display: underscores/hyphens → spaces, first letter of each
// word upper-cased (existing caps/acronyms like "NPS" preserved). Shared by the
// tab labels AND the per-metric card content so all custom-metric text reads
// consistently with the other report tabs (e.g. "demo11" → "Demo11",
// "custom_field" → "Custom Field").
const toTitleCase = (s: unknown): string =>
  (s ?? '')
    .toString()
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c: string) => c.toUpperCase())
    .trim();

// A metric's tab label: its Tab Name (set in Settings), else the metric name.
const getMetricTabLabel = (m: any): string =>
  toTitleCase((m?.tabName || '').toString().trim() || m?.name || 'Metric');

// Colors for custom-metric charts (reuses the channel palette hues).
const METRIC_CHART_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

// Format a scalar for display per the metric's Display Format + Unit.
const formatMetricValue = (v: unknown, displayFormat?: string, unit?: string): string => {
  const n = typeof v === 'number' ? v : Number(v) || 0;
  if (displayFormat === 'percentage') return `${n}%`;
  if (displayFormat === 'currency') return `$${n.toLocaleString()}`;
  if (displayFormat === 'duration') return analyticsService.formatDuration(n);
  return `${n.toLocaleString()}${unit ? ` ${unit}` : ''}`;
};

// One-line explanation shown under each tab's exported table title.
const REPORT_DESCRIPTIONS: Record<string, string> = {
  overview: 'Ticket volume and status distribution for the selected date range.',
  backtracked: 'Tickets that reached the final pipeline stage and were auto-archived (created in the selected range).',
  tickets: 'Ticket volume trends and priority breakdown, incl. total tickets (backtracked included).',
  agents: 'Per-agent workload and performance for the selected date range.',
  teams: 'Per-team tickets, resolved, SLA and first response; a parent team covers its sub-teams.',
  sla: 'SLA compliance for first response and resolution.',
  channels: 'Ticket distribution across channels for the selected range.',
  csat: 'Customer satisfaction (CSAT) and NPS survey results.',
  ai: 'AI chatbot conversation, resolution, and handoff metrics.',
  chat: 'Live chat session activity.',
  automations: 'Automation rules and their execution outcomes.',
  pipelines: 'Configured pipelines and their stages.',
  'custom-metrics': 'Configured custom metrics for this workspace.',
};

const STATUS_COLORS: Record<string, string> = {
  new: '#3B82F6',
  in_progress: '#06B6D4',
  pending: '#8B5CF6',
  resolved: '#10B981',
  closed: '#6B7280',
};

const CHANNEL_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

const PRIORITY_COLORS: Record<string, string> = {
  urgent: '#EF4444',
  high: '#F59E0B',
  medium: '#3B82F6',
  low: '#6B7280',
};

// ── Overview export: per-ticket detail table ────────────────────────────────
// The Overview export includes a second table listing every ticket created in
// the selected range, alongside the existing summary sheet.
interface TicketDetailRow {
  ticketNumber: string;
  subject: string;
  createdAt: string;
  status: string;
  resolved: string;
  slaBreached: string;
  slaDueAt: string;
  assignedTo: string;
}

// A ticket has breached its resolution SLA when it resolved after its due time,
// or is still open past due. Mirrors SlaIndicator's status logic.
function isResolutionSlaBreached(t: TicketEntity): boolean {
  if (!t.resolutionDueAt) return false;
  const due = new Date(t.resolutionDueAt).getTime();
  if (t.resolvedAt) return new Date(t.resolvedAt).getTime() > due;
  return Date.now() > due;
}

function toTicketDetailRow(t: TicketEntity): TicketDetailRow {
  // 'resolved' is the frontend's single terminal status ('closed' is normalized
  // to it); resolvedAt is the authoritative signal that the ticket is done.
  const resolved = t.status === 'resolved' || !!t.resolvedAt;
  return {
    ticketNumber: t.ticketNumber,
    subject: t.subject,
    createdAt: t.createdAt,
    status: t.status,
    resolved: resolved ? 'Yes' : 'No',
    slaBreached: !t.resolutionDueAt ? 'No SLA' : isResolutionSlaBreached(t) ? 'Yes' : 'No',
    slaDueAt: t.resolutionDueAt || '',
    assignedTo: t.assignee?.name || t.assignee?.email || (t.assigneeId ? '—' : 'Unassigned'),
  };
}

const ticketDetailColumns: ExportColumn<TicketDetailRow>[] = [
  { key: 'ticketNumber', header: 'Ticket Number' },
  { key: 'subject', header: 'Ticket Name' },
  { key: 'createdAt', header: 'Created At', formatter: (v) => formatters.dateTime(v) },
  { key: 'status', header: 'Status' },
  { key: 'resolved', header: 'Resolved' },
  { key: 'slaBreached', header: 'SLA Breached' },
  { key: 'slaDueAt', header: 'SLA Due Date', formatter: (v) => (v ? formatters.dateTime(v) : '') },
  { key: 'assignedTo', header: 'Assigned To' },
];

// ── Agent Performance export: per-agent roster table ────────────────────────
// Roster (role/status/onboarded) joined with each agent's ticket counts.
// "Designation" is not a stored field — only role exists.
interface AgentDetailRow {
  name: string;
  email: string;
  role: string;
  status: string;
  onboardedAt: string;
  ticketsHeld: number;
  ticketsWorkedOn: number;
  ticketsResolved: number;
}

const agentDetailColumns: ExportColumn<AgentDetailRow>[] = [
  { key: 'name', header: 'Agent Name' },
  { key: 'email', header: 'Email' },
  { key: 'role', header: 'Role' },
  { key: 'status', header: 'Status' },
  { key: 'onboardedAt', header: 'Onboarded', formatter: (v) => (v ? formatters.dateTime(v) : '') },
  { key: 'ticketsHeld', header: 'Tickets Held' },
  { key: 'ticketsWorkedOn', header: 'Tickets Worked On' },
  { key: 'ticketsResolved', header: 'Tickets Resolved' },
];

interface MetricCardProps {
  title: string;
  value: string | number;
  change?: number;
  changeLabel?: string;
  icon: React.ElementType;
  iconColor?: string;
  loading?: boolean;
  description?: string;
}

function MetricCard({ title, value, change, changeLabel, icon: Icon, iconColor = 'text-primary-500', loading, description }: MetricCardProps) {
  return (
    <div className="card p-6 bg-white dark:bg-gray-800">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{title}</p>
          {loading ? (
            <div className="mt-2 h-9 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
          ) : (
            <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{value}</p>
          )}
          {description && !loading && (
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{description}</p>
          )}
          {change !== undefined && !loading && (
            <div className="mt-2 flex items-center gap-1">
              {change >= 0 ? (
                <TrendingUp className="h-4 w-4 text-green-500" />
              ) : (
                <TrendingDown className="h-4 w-4 text-red-500" />
              )}
              <span className={`text-sm font-medium ${change >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600'}`}>
                {change >= 0 ? '+' : ''}{change}%
              </span>
              {changeLabel && (
                <span className="text-sm text-gray-500 dark:text-gray-400">{changeLabel}</span>
              )}
            </div>
          )}
        </div>
        <div className={`p-3 rounded-lg bg-gray-100 dark:bg-gray-700 ${iconColor}`}>
          <Icon className="h-6 w-6" />
        </div>
      </div>
    </div>
  );
}

interface ChartCardProps {
  title: string;
  description?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
  loading?: boolean;
}

function ChartCard({ title, description, children, action, loading }: ChartCardProps) {
  return (
    <div className="card bg-white dark:bg-gray-800">
      <div className="flex items-start justify-between gap-3 p-4 border-b border-gray-200 dark:border-gray-700">
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-white">{title}</h3>
          {description && (
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{description}</p>
          )}
        </div>
        {action}
      </div>
      <div className="p-4">
        {loading ? (
          <div className="h-[300px] flex items-center justify-center">
            <RefreshCw className="h-8 w-8 text-gray-400 animate-spin dark:text-gray-500" />
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}

export default function ReportsPage() {
  const [searchParams] = useSearchParams();
  // Gridlines and axis labels follow the theme (a fixed light hex glares in dark).
  const chart = useThemeColors({ grid: '--co-line', axis: '--co-ink-3' });
  const defaultRange = useMemo(() => getDefaultDateRange(), []);

  // Read date filters from URL params (from AI search bar drill-down)
  const urlStartDate = searchParams.get('createdAfter') || searchParams.get('startDate');
  const urlEndDate = searchParams.get('createdBefore') || searchParams.get('endDate');

  // Parse and format dates from URL, or use defaults
  const initialStartDate = useMemo(() => {
    if (urlStartDate) {
      try {
        const date = new Date(urlStartDate);
        return date.toISOString().split('T')[0];
      } catch {
        return defaultRange.start;
      }
    }
    return defaultRange.start;
  }, [urlStartDate, defaultRange.start]);

  const initialEndDate = useMemo(() => {
    if (urlEndDate) {
      try {
        const date = new Date(urlEndDate);
        return date.toISOString().split('T')[0];
      } catch {
        return defaultRange.end;
      }
    }
    return defaultRange.end;
  }, [urlEndDate, defaultRange.end]);

  const [startDate, setStartDate] = useState<string>(initialStartDate);
  const [endDate, setEndDate] = useState<string>(initialEndDate);
  const todayStr = defaultRange.end;

  // Whole days inclusive, so a start and end on the same date reads "1 day"
  // rather than "0 days". Parsed as UTC noon: a plain `new Date('2026-01-01')`
  // is midnight UTC, and in a negative-offset timezone that lands on the day
  // before, shortening every range by one.
  const spanLabel = useMemo(() => {
    const day = (iso: string) => {
      const [y, m, d] = iso.split('-').map(Number);
      return Number.isFinite(y) && Number.isFinite(m) && Number.isFinite(d)
        ? Date.UTC(y, m - 1, d, 12)
        : NaN;
    };
    const from = day(startDate);
    const to = day(endDate);
    if (!Number.isFinite(from) || !Number.isFinite(to) || to < from) return '—';
    const days = Math.round((to - from) / 86_400_000) + 1;
    return `${days} ${days === 1 ? 'day' : 'days'}`;
  }, [startDate, endDate]);

  // Initialize from URL param if present, otherwise default to 'overview'
  const initialReport = searchParams.get('report') || 'overview';
  const [selectedReport, setSelectedReport] = useState(initialReport);
  // Team filter: the figures cover that team and its sub-teams.
  const [teamFilter, setTeamFilter] = useState<string | undefined>(undefined);
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const userRole = user?.role || 'agent';
  const userScope = getUserScope(userRole);
  const isAgent = userScope === 'self';

  // An agent's view is "My Data": tabs whose figures are organization-wide
  // (agent comparison, AI chatbot, automations, pipelines, custom metrics) are
  // not offered, rather than shown unscoped under that label.
  const availableReportTypes = useMemo(() =>
    isAgent ? REPORT_TYPES.filter(r => !ORG_WIDE_REPORT_IDS.has(r.id)) : REPORT_TYPES,
    [isAgent],
  );

  const dateRange = useMemo<DateRange>(() => ({ start: startDate, end: endDate }), [startDate, endDate]);

  // Span of the selected window in days — drives trend granularity and AI look-back,
  // preserving the prior preset behavior (<=7 days → daily buckets, else weekly).
  const spanDays = useMemo(() => {
    const ms = new Date(endDate).getTime() - new Date(startDate).getTime();
    return Number.isFinite(ms) ? Math.max(0, Math.round(ms / 86_400_000)) : 0;
  }, [startDate, endDate]);

  // Fetch real data
  const { data: metrics, isLoading: metricsLoading } = useDashboardMetrics(dateRange, false, teamFilter);
  // reportView: full-roster Agent Performance list — every org member
  // (agents, supervisors and admins), incl. zero-ticket ones. Scoped to this
  // page only; the dashboard widgets keep the original ticket-derived behaviour.
  const { data: agentData, isLoading: agentsLoading } = useAgentMetrics(dateRange, {
    reportView: true,
    teamId: teamFilter,
  });
  const { data: slaData, isLoading: slaLoading } = useSlaMetrics(dateRange, teamFilter);
  const { data: trendsData, isLoading: trendsLoading } = useTicketTrends(
    spanDays <= 7 ? 'day' : 'week',
    dateRange,
    teamFilter,
  );
  const { data: teamPerformance, isLoading: teamPerformanceLoading } = useTeamPerformance(
    dateRange,
    selectedReport === 'teams',
  );

  // Cross-service data
  const { data: csatStats, isLoading: csatLoading } = useCsatStats(dateRange);
  const { data: aiAnalytics, isLoading: aiLoading } = useAiChatbotAnalytics(
    spanDays <= 1 ? 1 : spanDays <= 7 ? 7 : 30,
  );
  const { data: chatStats, isLoading: chatLoading } = useChatStats(selectedReport === 'chat');
  const { data: automationRules, isLoading: rulesLoading } = useAutomationRuleStats();
  const { data: automationExecs, isLoading: execsLoading } = useAutomationExecutionStats();
  const { data: pipelines, isLoading: pipelinesLoading } = usePipelines();
  const { data: customMetrics, isLoading: customMetricsLoading } = useCustomMetricsList(true);

  // One dynamic Reports tab per ACTIVE custom metric (id = prefix + metric id,
  // label = its Tab Name or the metric name). Order follows the metrics list.
  const customMetricTabTypes = useMemo(
    () =>
      (customMetrics || [])
        .filter((m: any) => m.isActive)
        .map((m: any) => ({ id: `${CUSTOM_METRIC_PREFIX}${m.id}`, label: getMetricTabLabel(m), icon: Gauge })),
    [customMetrics],
  );

  // The full ordered tab list: static report types (minus the Custom Metrics
  // overview) → one tab per metric → the Custom Metrics overview LAST.
  const reportTabs = useMemo(() => {
    const staticTabs = availableReportTypes.filter((t) => t.id !== 'custom-metrics');
    const overviewTab = availableReportTypes.find((t) => t.id === 'custom-metrics');
    // Per-metric tabs are organization KPIs too: none for an agent.
    return [...staticTabs, ...(isAgent ? [] : customMetricTabTypes), ...(overviewTab ? [overviewTab] : [])];
  }, [availableReportTypes, customMetricTabTypes, isAgent]);

  // If a metric tab is selected but its metric no longer exists (deleted /
  // deactivated), fall back to the Custom Metrics overview so the body never blanks.
  useEffect(() => {
    if (!selectedReport.startsWith(CUSTOM_METRIC_PREFIX) || customMetricsLoading) return;
    if (!customMetricTabTypes.some((t) => t.id === selectedReport)) {
      setSelectedReport('custom-metrics');
    }
  }, [selectedReport, customMetricTabTypes, customMetricsLoading]);

  const navigate = useNavigate();
  const setTicketFilters = useTicketsListStore((s) => s.setFilters);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showGeneratedReports, setShowGeneratedReports] = useState(false);
  // The "Scheduler Reports" button appears only when at least one Report
  // Generation job has "Show on Reports page" (deliverUi) enabled — i.e. the
  // tenant actually opted into UI-visible scheduled reports. Best-effort: if
  // the jobs call is unavailable (permissions/tier), keep the button hidden.
  const [hasUiReportJob, setHasUiReportJob] = useState(false);
  const loading = metricsLoading;

  useEffect(() => {
    let active = true;
    workflowService
      .getJobs({ limit: 100 })
      .then((res) => {
        if (!active) return;
        const has = (res.data || []).some(
          (j) =>
            String(j.jobType).toLowerCase() === 'report_generation' &&
            (j.jobConfig as Record<string, unknown> | undefined)?.deliverUi === true,
        );
        setHasUiReportJob(has);
      })
      .catch(() => {
        if (active) setHasUiReportJob(false);
      });
    return () => {
      active = false;
    };
  }, []);

  /** Navigate to ticket list with filters applied (drill-down from charts) */
  const drillDown = (filters: Record<string, string | undefined>) => {
    setTicketFilters({
      ...filters,
      page: 1,
      limit: 20,
      sortBy: 'updatedAt',
      sortOrder: 'desc',
    } as any);
    navigate('/tickets/list');
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await queryClient.invalidateQueries({ queryKey: analyticsKeys.all });
    setIsRefreshing(false);
  };

  // Derive display data from API responses
  const totalTickets = metrics?.tickets.total || 0;
  const resolvedTickets = metrics?.tickets.resolved || 0;
  const closedTickets = metrics?.tickets.closed || 0;
  const backtrackedTickets = metrics?.tickets.backtracked || 0;
  // Assignment split (created-in-range, incl. backtracked → sums to Total).
  const assignedTickets = metrics?.tickets.assigned || 0;
  const unassignedTickets = metrics?.tickets.unassigned || 0;
  const assignmentTotal = assignedTickets + unassignedTickets;
  const assignmentDistribution = [
    { name: 'Assigned', value: assignedTickets, color: '#10B981' },
    { name: 'Unassigned', value: unassignedTickets, color: '#9CA3AF' },
  ];
  // Overview "Resolved" KPI = all completed tickets: resolved + closed + backtracked.
  // A proper subset of Total Tickets (both createdAt-based, role-scoped, incl. backtracked).
  // `resolvedTickets` alone still feeds the status charts/distribution below (unchanged).
  const completedTickets = resolvedTickets + closedTickets + backtrackedTickets;
  // performance.avg* are in milliseconds (BFF converts the analytics service's
  // hours via *3_600_000); formatDuration expects minutes, so convert ms→min.
  const avgResponseTime = metrics ? analyticsService.formatDuration(metrics.performance.avgFirstResponseTime / 60000) : '-';
  const avgResolutionTime = metrics ? analyticsService.formatDuration(metrics.performance.avgResolutionTime / 60000) : '-';
  const slaCompliance = metrics?.performance.firstResponseSlaCompliance || 0;

  // Status distribution for pie chart
  const statusDistribution = metrics ? [
    { name: 'New', value: metrics.tickets.new, color: STATUS_COLORS.new },
    { name: 'In Progress', value: metrics.tickets.in_progress, color: STATUS_COLORS.in_progress },
    { name: 'Pending', value: metrics.tickets.pending, color: STATUS_COLORS.pending },
    { name: 'Resolved', value: metrics.tickets.resolved, color: STATUS_COLORS.resolved },
  ].filter(s => s.value > 0) : [];

  // Priority distribution for pie/bar charts. Always show all four priorities
  // (low, medium, high, urgent) in a fixed order, defaulting missing ones to 0
  // so the charts stay consistent even when a priority has no tickets.
  const priorityDistribution = metrics ? ['low', 'medium', 'high', 'urgent'].map((name) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1),
    value: metrics.priorities[name] ?? 0,
    color: PRIORITY_COLORS[name] || '#6B7280',
  })) : [];
  // Sum across the four priorities — used as the single denominator for both
  // the pie slices and the legend so their percentages always agree.
  const prioritySum = priorityDistribution.reduce((sum, p) => sum + p.value, 0);

  // Channel data — two distinct views:
  //  • channelData (totals): every ticket CREATED per channel in range, INCLUDING
  //    backtracked → the KPI cards + export.
  //  • channelOpen: only still-OPEN tickets per channel (excludes resolved/closed/
  //    backtracked) → the bar + pie charts.
  // Falls back to the legacy `channels` map if an older BFF hasn't shipped the split.
  const toChannelRows = (map?: Record<string, number>) => {
    const entries = map ? Object.entries(map) : [];
    const total = entries.reduce((sum, [, v]) => sum + v, 0);
    return entries.map(([name, tickets]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1),
      tickets,
      percentage: total > 0 ? Math.round((tickets / total) * 100) : 0,
    }));
  };
  const channelData = toChannelRows(metrics?.channelsTotal ?? metrics?.channels);
  const channelOpen = toChannelRows(metrics?.channelsOpen ?? metrics?.channels);

  // Agent data for the table + export: EVERY agent in the org (no cap), so the
  // full roster is visible and exportable — not just the busiest 10.
  const agentPerformance = (agentData || []).map(a => ({
    name: a.agentName,
    ticketsAssigned: a.ticketsAssigned,
    // As assignee or secondary agent; older BFFs omit it, so fall back to assigned.
    ticketsWorkedOn: a.ticketsWorkedOn ?? a.ticketsAssigned,
    ticketsResolved: a.ticketsResolved,
    // Minutes, or null when there's no responded/resolved ticket to average —
    // kept null so the table can show "–" instead of a misleading "0 min".
    avgResponseTime: a.avgResponseTime == null ? null : Math.round(a.avgResponseTime),
    avgResolutionTime: a.avgResolutionTime == null ? null : Math.round(a.avgResolutionTime),
    slaCompliance: Math.round(a.slaCompliance),
    satisfaction: a.customerSatisfaction || 0,
  }));
  // The comparison bar chart stays capped to the busiest 10 for readability
  // (a fixed-height vertical chart can't render dozens of bars legibly).
  // Zero-activity members are left to the table: they all tie at 0 and in an org
  // over 10 people they filled the spare slots in arbitrary roster order, so some
  // empty rows showed while other members (e.g. the newest agents) silently didn't.
  const agentChartData = agentPerformance
    .filter((a) => a.ticketsAssigned > 0 || a.ticketsWorkedOn > 0)
    .slice(0, 10);

  // SLA chart data
  const slaChartData = slaData ? [
    { name: 'First Response', metSLA: slaData.firstResponse.percentage, breached: 100 - slaData.firstResponse.percentage },
    { name: 'Resolution', metSLA: slaData.resolution.percentage, breached: 100 - slaData.resolution.percentage },
  ] : [];

  const overallSla = slaData
    ? ((slaData.firstResponse.percentage + slaData.resolution.percentage) / 2).toFixed(1)
    : '-';
  // Distinct breached tickets from the backend. Fall back to the per-dimension
  // sum only if the field is absent (older BFF/analytics) — the sum double-counts
  // tickets that breach both first-response AND resolution.
  const totalBreached = slaData
    ? (slaData.breachedTickets ?? slaData.firstResponse.breached + slaData.resolution.breached)
    : 0;

  // Ticket trends for line/area charts
  const ticketTrends = trendsData?.map(t => ({
    date: t.period,
    created: t.created,
    resolved: t.resolved,
    pending: 0,
  })) || [];

  // Export column definitions
  const ticketVolumeColumns: ExportColumn<{ date: string; created: number; resolved: number; pending: number }>[] = [
    { key: 'date', header: 'Date' },
    { key: 'created', header: 'Created' },
    { key: 'resolved', header: 'Resolved' },
    { key: 'pending', header: 'Pending' },
  ];

  const overviewColumns: ExportColumn<{ date: string; created: number; resolved: number; pending: number }>[] = [
    { key: 'date', header: 'Date/Status' },
    { key: 'created', header: 'Created/Count' },
    { key: 'resolved', header: 'Resolved' },
    { key: 'pending', header: 'Pending' },
  ];

  // Match the on-screen table: raw minutes (e.g. 63665) are unreadable, so show
  // a human duration ("44d 5h"); null times show "N/A" (not a blank cell), and a
  // 0 satisfaction means "no ratings" → "N/A" rather than a misleading 0.
  const durationOrNa = (v: unknown): string =>
    v === null || v === undefined ? 'N/A' : analyticsService.formatDuration(v as number);
  const satisfactionOrNa = (v: unknown): string =>
    typeof v === 'number' && v > 0 ? v.toFixed(1) : 'N/A';
  const agentPerformanceColumns: ExportColumn<{ name: string; ticketsAssigned: number; ticketsWorkedOn: number; ticketsResolved: number; avgResponseTime: number | null; avgResolutionTime: number | null; slaCompliance: number; satisfaction: number }>[] = [
    { key: 'name', header: 'Agent Name' },
    { key: 'ticketsAssigned', header: 'Tickets Assigned' },
    { key: 'ticketsWorkedOn', header: 'Tickets Worked On' },
    { key: 'ticketsResolved', header: 'Tickets Resolved' },
    { key: 'avgResponseTime', header: 'Avg Response Time', formatter: durationOrNa },
    { key: 'avgResolutionTime', header: 'Avg Resolution Time', formatter: durationOrNa },
    { key: 'slaCompliance', header: 'SLA Compliance (%)' },
    { key: 'satisfaction', header: 'Satisfaction Score', formatter: satisfactionOrNa },
  ];

  // Round percentages for the download so raw floats (e.g. 100 - 11.2222… =
  // 88.7777…) don't print with a long decimal tail. Matches the 1-decimal
  // precision shown on screen; header already carries the "(%)" unit.
  const roundPercent = (v: unknown): string =>
    v === null || v === undefined || v === '' ? '' : Number(v).toFixed(1);
  const slaColumns: ExportColumn<{ name: string; metSLA: number; breached: number }>[] = [
    { key: 'name', header: 'SLA Type' },
    { key: 'metSLA', header: 'Met SLA (%)', formatter: roundPercent },
    { key: 'breached', header: 'Breached (%)', formatter: roundPercent },
  ];

  const channelColumns: ExportColumn<{ name: string; tickets: number; percentage: number }>[] = [
    { key: 'name', header: 'Channel' },
    { key: 'tickets', header: 'Ticket Count' },
    { key: 'percentage', header: 'Percentage (%)' },
  ];

  // Get export data and columns based on selected report
  const exportConfig = useMemo(() => {
    // Dynamic per-metric tab: export just that single metric's definition.
    if (selectedReport.startsWith(CUSTOM_METRIC_PREFIX)) {
      const metricId = selectedReport.slice(CUSTOM_METRIC_PREFIX.length);
      const m = (customMetrics || []).find((x: any) => x.id === metricId);
      const label = m ? getMetricTabLabel(m) : 'Metric';
      return {
        data: m
          ? [{
              name: m.name,
              dataSource: m.dataSource,
              metricType: m.metricType,
              category: m.category || 'uncategorized',
              active: m.isActive ? 'Yes' : 'No',
            }]
          : [],
        columns: [
          { key: 'name' as const, header: 'Metric Name' },
          { key: 'dataSource' as const, header: 'Data Source' },
          { key: 'metricType' as const, header: 'Metric Type' },
          { key: 'category' as const, header: 'Category' },
          { key: 'active' as const, header: 'Active' },
        ],
        filename: `custom_metric_${label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') || 'metric'}_report`,
        sheetName: label,
      };
    }
    switch (selectedReport) {
      case 'overview':
        return {
          data: [...ticketTrends, ...statusDistribution.map(s => ({
            date: s.name,
            created: s.value,
            resolved: 0,
            pending: 0,
          }))],
          columns: overviewColumns,
          filename: 'overview_report',
          sheetName: 'Overview',
        };
      case 'backtracked':
        return {
          data: [
            { metric: 'Backtracked', value: backtrackedTickets },
            {
              metric: 'Backtracked Rate',
              value: totalTickets > 0 ? `${Math.round((backtrackedTickets / totalTickets) * 100)}%` : '0%',
            },
          ],
          columns: [
            { key: 'metric' as const, header: 'Metric' },
            { key: 'value' as const, header: 'Value' },
          ],
          filename: 'backtracked_report',
          sheetName: 'Backtracked',
        };
      case 'agents':
        return {
          data: agentPerformance,
          columns: agentPerformanceColumns,
          filename: 'agent_performance_report',
          sheetName: 'Agent Performance',
        };
      case 'teams':
        return {
          data: orderTeamRows(teamPerformance ?? []).map((row) => ({
            team: row.depth === 1 ? `  ${row.name}` : row.name,
            agents: row.agentCount,
            tickets: row.ticketsAssigned,
            resolved: row.ticketsResolved,
            slaCompliance: row.slaCompliance === null ? '' : Math.round(row.slaCompliance),
            avgFirstResponseMinutes: row.avgResponseTime === null ? '' : Math.round(row.avgResponseTime),
          })),
          columns: [
            { key: 'team' as const, header: 'Team' },
            { key: 'agents' as const, header: 'Agents' },
            { key: 'tickets' as const, header: 'Tickets' },
            { key: 'resolved' as const, header: 'Resolved' },
            { key: 'slaCompliance' as const, header: 'SLA %' },
            { key: 'avgFirstResponseMinutes' as const, header: 'Avg first response (min)' },
          ],
          filename: 'team_performance_report',
          sheetName: 'Team Performance',
        };
      case 'sla':
        return {
          data: slaChartData,
          columns: slaColumns,
          filename: 'sla_compliance_report',
          sheetName: 'SLA Compliance',
        };
      case 'channels':
        return {
          data: channelData,
          columns: channelColumns,
          filename: 'channel_analysis_report',
          sheetName: 'Channel Analysis',
        };
      case 'csat':
        return {
          data: csatStats ? [
            // Round floats to 1 decimal so the download matches the on-screen
            // cards (avg rating / response rate use .toFixed(1)) instead of
            // printing a long decimal tail. Number(...) keeps integers clean.
            { label: 'Avg Rating', value: Number(csatStats.avgRating.toFixed(1)), count: 0, rate: 0 },
            { label: 'NPS Score', value: Number(csatStats.nps.toFixed(1)), count: 0, rate: 0 },
            { label: 'Response Rate', value: 0, count: 0, rate: Number(csatStats.responseRate.toFixed(1)) },
            { label: 'Completed', value: csatStats.completed, count: csatStats.completed, rate: 0 },
            { label: 'Pending', value: csatStats.pending, count: csatStats.pending, rate: 0 },
            { label: 'Expired', value: csatStats.expired, count: csatStats.expired, rate: 0 },
          ] : [],
          columns: [
            { key: 'label' as const, header: 'Metric' },
            { key: 'value' as const, header: 'Value' },
            { key: 'count' as const, header: 'Count' },
            { key: 'rate' as const, header: 'Rate (%)' },
          ],
          filename: 'csat_nps_report',
          sheetName: 'CSAT / NPS',
        };
      case 'ai':
        return {
          data: aiAnalytics ? [
            { metric: 'Total Conversations', value: aiAnalytics.total_conversations },
            { metric: 'Resolution Rate', value: `${(aiAnalytics.resolution_rate * 100).toFixed(1)}%` },
            { metric: 'Handoff Rate', value: `${(aiAnalytics.handoff_rate * 100).toFixed(1)}%` },
            { metric: 'Avg Confidence', value: `${(aiAnalytics.avg_confidence * 100).toFixed(1)}%` },
            { metric: 'Total Messages', value: aiAnalytics.total_messages },
            { metric: 'Total Handoffs', value: aiAnalytics.total_handoffs },
            { metric: 'Total Resolutions', value: aiAnalytics.total_resolutions },
          ] : [],
          columns: [
            { key: 'metric' as const, header: 'Metric' },
            { key: 'value' as const, header: 'Value' },
          ],
          filename: 'ai_chatbot_report',
          sheetName: 'AI Chatbot',
        };
      case 'chat':
        return {
          data: chatStats ? [
            { metric: 'Waiting', value: chatStats.waiting },
            { metric: 'Active Sessions', value: chatStats.active },
            { metric: 'Ended Today', value: chatStats.endedToday },
          ] : [],
          columns: [
            { key: 'metric' as const, header: 'Metric' },
            { key: 'value' as const, header: 'Value' },
          ],
          filename: 'live_chat_report',
          sheetName: 'Live Chat',
        };
      case 'automations':
        return {
          data: [
            ...(automationRules ? [
              { metric: 'Total Rules', value: automationRules.total },
              { metric: 'Active Rules', value: automationRules.active },
            ] : []),
            ...(automationExecs ? [
              { metric: 'Total Executions', value: automationExecs.total },
              { metric: 'Completed', value: automationExecs.completed },
              { metric: 'Failed', value: automationExecs.failed },
              { metric: 'Pending', value: automationExecs.pending },
              { metric: 'Running', value: automationExecs.running },
            ] : []),
          ],
          columns: [
            { key: 'metric' as const, header: 'Metric' },
            { key: 'value' as const, header: 'Value' },
          ],
          filename: 'automations_report',
          sheetName: 'Automations',
        };
      case 'pipelines':
        return {
          data: pipelines
            ? pipelines.flatMap((p: any) =>
                (p.stages || []).map((s: any) => ({
                  pipeline: p.name,
                  stage: s.name,
                  order: s.sortOrder,
                })),
              )
            : [],
          columns: [
            { key: 'pipeline' as const, header: 'Pipeline' },
            { key: 'stage' as const, header: 'Stage' },
            { key: 'order' as const, header: 'Stage Order' },
          ],
          filename: 'pipeline_analysis_report',
          sheetName: 'Pipeline Analysis',
        };
      case 'custom-metrics':
        return {
          // Overview tab exports the full roster; each metric also exports from
          // its own per-metric tab (handled above).
          data: (customMetrics || [])
            .map((m: any) => ({
              name: m.name,
              tab: getMetricTabLabel(m),
              dataSource: m.dataSource,
              metricType: m.metricType,
              category: m.category || 'uncategorized',
              active: m.isActive ? 'Yes' : 'No',
            })),
          columns: [
            { key: 'name' as const, header: 'Metric Name' },
            { key: 'tab' as const, header: 'Reports Tab' },
            { key: 'dataSource' as const, header: 'Data Source' },
            { key: 'metricType' as const, header: 'Metric Type' },
            { key: 'category' as const, header: 'Category' },
            { key: 'active' as const, header: 'Active' },
          ],
          filename: 'custom_metrics_report',
          sheetName: 'Custom Metrics',
        };
      case 'tickets':
      default:
        return {
          data: [
            // Totals first — Total Tickets INCLUDES backtracked (createdAt-scoped,
            // role-scoped), with the backtracked subset called out explicitly.
            { date: 'Total Tickets (incl. backtracked)', created: totalTickets, resolved: resolvedTickets, pending: 0 },
            { date: 'Of which Backtracked', created: backtrackedTickets, resolved: 0, pending: 0 },
            ...ticketTrends,
            ...priorityDistribution.map(p => ({
              date: `Priority: ${p.name}`,
              created: p.value,
              resolved: 0,
              pending: 0,
            })),
          ],
          columns: ticketVolumeColumns,
          filename: 'ticket_analytics_report',
          sheetName: 'Ticket Analytics',
        };
    }
  }, [selectedReport, teamPerformance, ticketTrends, statusDistribution, priorityDistribution, agentPerformance, slaChartData, channelData, channelOpen, csatStats, aiAnalytics, chatStats, automationRules, automationExecs, pipelines, customMetrics, backtrackedTickets, totalTickets, resolvedTickets]);

  // Generic "Ticket Details" table appended to every tab's export: lists every
  // ticket created in the selected range (number, name, created-at, status,
  // resolved, SLA breach + due date, assignee). Fetched lazily — only when the
  // user exports — and paginated so it isn't truncated to one page. Role scope
  // is enforced server-side (agents see only their own tickets).
  const buildTicketDetailSheets = useCallback(async (): Promise<ExportTable<TicketDetailRow>[]> => {
    const PAGE_SIZE = 200;
    const MAX_PAGES = 50; // safety cap (≤10k tickets) so a huge range can't hang the export
    const baseFilters = {
      createdAfter: `${startDate}T00:00:00.000Z`,
      createdBefore: `${endDate}T23:59:59.999Z`,
      // Include backtracked tickets so the detail row count matches the on-screen
      // "Total Tickets" KPI, which counts them too. The list endpoint excludes
      // backtracked tickets by default (#10122).
      includeBacktracked: true,
      sortBy: 'createdAt',
      sortOrder: 'desc' as const,
    };
    let rows: TicketDetailRow[] = [];
    try {
      const first = await ticketService.getTickets({ ...baseFilters, page: 1, limit: PAGE_SIZE });
      const totalPages = Math.min(
        MAX_PAGES,
        Math.max(1, Math.ceil((first.total || 0) / (first.limit || PAGE_SIZE))),
      );
      let all = first.data || [];
      if (totalPages > 1) {
        const rest = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) =>
            ticketService.getTickets({ ...baseFilters, page: i + 2, limit: PAGE_SIZE }),
          ),
        );
        all = [...all, ...rest.flatMap((r) => r.data || [])];
      }
      rows = all.map(toTicketDetailRow);
    } catch (err) {
      // Don't block the primary export if the ticket fetch fails — still emit
      // the Ticket Details section (empty) so it's visibly present.
      console.warn('[Reports] Failed to build ticket-details export sheet', err);
    }
    // Always return the table so the "Ticket Details" section appears in the
    // export (with headers) even when there are no tickets in range.
    return [
      {
        sheetName: 'Ticket Details',
        description: 'One row per ticket created in the selected date range (includes backtracked tickets, matching the Total Tickets figure).',
        columns: ticketDetailColumns,
        data: rows,
      },
    ];
  }, [startDate, endDate]);

  // Agent Performance export: a single "Agent Details" roster table (role,
  // status, onboarded date, tickets held + resolved) joined from the org roster
  // and the agent metrics already on the page. No ticket table here.
  const buildAgentDetailSheets = useCallback(async (): Promise<ExportTable<AgentDetailRow>[]> => {
    const metricsById = new Map((agentData || []).map((a) => [a.agentId, a]));
    let agentRows: AgentDetailRow[] = [];
    try {
      // Org rosters are small; a single page (cap 100) covers them.
      const res = await teamMembersService.getTeamMembers({ limit: 100 });
      agentRows = (res.data || []).map((m) => {
        const metric = metricsById.get(m.id);
        const name =
          m.displayName || [m.firstName, m.lastName].filter(Boolean).join(' ') || m.email;
        return {
          name,
          email: m.email,
          role: m.role,
          status: m.status,
          onboardedAt: m.createdAt,
          ticketsHeld: metric?.ticketsAssigned ?? 0,
          ticketsWorkedOn: metric?.ticketsWorkedOn ?? metric?.ticketsAssigned ?? 0,
          ticketsResolved: metric?.ticketsResolved ?? 0,
        };
      });
    } catch (err) {
      console.warn('[Reports] Failed to build agent-details export sheet', err);
    }
    return [
      {
        sheetName: 'Agent Details',
        description: 'Every team member with role, status, onboarding date, and ticket counts.',
        columns: agentDetailColumns,
        data: agentRows,
      },
    ];
  }, [agentData]);

  // Reusable single-metric card (title + description + category badge + the
  // live evaluated result). Used by each per-metric tab.
  const renderMetricCard = (metric: any) => (
    <div className="card bg-white dark:bg-gray-800 p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h4 className="font-semibold text-gray-900 dark:text-white">{toTitleCase(metric.name)}</h4>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {metric.description || `${toTitleCase(metric.dataSource)} / ${toTitleCase(metric.metricType)}`}
          </p>
        </div>
        <span className="text-xs px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
          {toTitleCase(metric.category || 'uncategorized')}
        </span>
      </div>
      <CustomMetricReportCard metric={metric} dateRange={dateRange} />
    </div>
  );

  // The "Custom Metrics" tab is now an OVERVIEW/landing (summary + guidance);
  // the individual metric charts live in their own per-metric tabs.
  const renderCustomMetricsOverview = () => {
    if (customMetricsLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 text-gray-400 animate-spin dark:text-gray-500" />
        </div>
      );
    }
    const active = (customMetrics || []).filter((m: any) => m.isActive);
    if (active.length === 0) {
      return (
        <div className="card p-12 text-center bg-white dark:bg-gray-800">
          <Gauge className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">No custom metrics</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Create custom metrics in Settings — each one gets its own tab here.
          </p>
          <button
            onClick={() => navigate('/settings/custom-metrics')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg text-sm hover:bg-primary-700 transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
            Go to Settings
          </button>
        </div>
      );
    }
    return (
      <>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <MetricCard title="Active Metrics" value={active.length} icon={Gauge} iconColor="text-emerald-500" />
          <MetricCard title="Total Defined" value={(customMetrics || []).length} icon={BarChart3} iconColor="text-blue-500" />
          <MetricCard
            title="Data Sources"
            value={new Set(active.map((m: any) => m.dataSource)).size}
            icon={Activity}
            iconColor="text-purple-500"
          />
        </div>
        <div className="card bg-white dark:bg-gray-800 p-6">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Each custom metric has its own tab above. Select a metric's tab to view its chart.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {active.map((m: any) => (
              <button
                key={m.id}
                onClick={() => setSelectedReport(`${CUSTOM_METRIC_PREFIX}${m.id}`)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-200 transition-colors"
              >
                <Gauge className="h-3.5 w-3.5" />
                {getMetricTabLabel(m)}
              </button>
            ))}
          </div>
        </div>
      </>
    );
  };

  // The metric backing the active per-metric tab (if any).
  const activeCustomMetricId = selectedReport.startsWith(CUSTOM_METRIC_PREFIX)
    ? selectedReport.slice(CUSTOM_METRIC_PREFIX.length)
    : null;
  const activeCustomMetric = activeCustomMetricId
    ? (customMetrics || []).find((m: any) => m.id === activeCustomMetricId && m.isActive)
    : null;

  // The page ground is the shell's own `canvas`, which is what every other
  // screen sits on — /customers, /organizations, the dashboard. Reports gets no
  // special ground, so it blends instead of announcing itself.
  //
  // The dark band that used to run down the gutter was never the tint: it was a
  // shadow this page briefly overrode to roughly twice its normal depth, thrown
  // across the same 14px corridor the nav rail throws its own shadow into. With
  // the shared `--co-shadow-card` back, the two lifts are gentle enough that the
  // gutter reads as a gutter.
  return (
    <div className="co-reports flex h-full min-h-0 flex-col gap-[14px] bg-[var(--co-canvas)] py-4 pr-4">
      {/* Head card — 26c. The title block and the Refresh/Export controls keep
          their exact behaviour; only the surface under them changed, from a
          full-bleed bar with a bottom rule to a card floating on the canvas. */}
      <div className="relative flex flex-none flex-col">
      <div
        className={`${CARD_CLASS} relative px-5 pb-2 pt-[14px] 2xl:!rounded-br-none`}
        style={{ ...CARD_STYLE, borderRadius: 14 }}
      >
        <div className="flex items-center justify-between gap-[10px]">
        <div className="min-w-0">
          <h1 className="text-[28px] font-bold leading-[1.1] tracking-[-.02em] text-[var(--co-ink)]">
            {isAgent ? 'My Reports' : 'Reports'}
          </h1>
          <div className="flex flex-wrap items-center gap-[10px] mt-[4px]">
            <p className="text-[13px] text-[var(--co-ink-2)]">
              {isAgent
                ? 'Analyze your personal metrics and performance'
                : 'Analyze your support metrics and performance'}
            </p>
            <span className="co-mono inline-flex items-center gap-[6px] rounded-full border border-[var(--co-brass-line)] bg-[var(--co-selected)] px-[9px] py-[3px] text-[9.5px] font-semibold uppercase tracking-[.1em] text-[var(--co-brass-ink)]">
              <Shield className="w-3 h-3" />
              {isAgent ? 'My Data' : userScope === 'team' ? 'Team' : 'All Data'}
            </span>
          </div>
        </div>
        <div className="flex h-[34px] flex-none items-center">
          <PageHeaderMeta />
        </div>
        </div>
        {/* Row 2: the page actions, right-aligned. The report window lives in
            the Period tab under the card's bottom-right corner (the Dashboard
            shape). */}
        <div className="mt-1.5 flex flex-wrap items-center justify-end gap-[10px] border-t border-[var(--co-line-subtle)] pt-2">
          {hasUiReportJob && (
            <button
              onClick={() => setShowGeneratedReports(true)}
              className="btn-secondary !h-9 !py-0 rounded-[10px]"
              title="View reports produced by scheduled Report Generation jobs"
            >
              <FileClock className="h-4 w-4 mr-2" />
              Scheduler Reports
            </button>
          )}
          <button
            onClick={handleRefresh}
            className="btn-secondary !h-9 !py-0 rounded-[10px]"
            disabled={loading || isRefreshing}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading || isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <ExportButton
            data={exportConfig.data}
            columns={exportConfig.columns as ExportColumn<Record<string, unknown>>[]}
            filename={exportConfig.filename}
            sheetName={exportConfig.sheetName}
            primaryTitle={exportConfig.sheetName}
            primaryDescription={REPORT_DESCRIPTIONS[selectedReport]}
            getExtraSheets={
              selectedReport === 'overview'
                ? buildTicketDetailSheets
                : selectedReport === 'agents'
                  ? buildAgentDetailSheets
                  : undefined
            }
            variant="primary"
            className="!h-9 !py-0 !rounded-[10px]"
          />
        </div>
      </div>
      </div>

      {/* Period tab + AI bar, one row in the page flow. Wide screens (2xl): the
          Period tab hangs off the head card's bottom-right edge (pulled up over
          the page gap to join it) with the AI bar beside it, bottom-aligned.
          Tablet and below: two full-width cards, Period first.

          The tab used to be absolutely positioned at a fixed 640px. When its
          controls didn't fit on one line (team names, wider date fields, zoom)
          it wrapped to two, grew taller than the space it had, and covered the
          report tabs below. In the flow, a wrap only makes this row taller. */}
      <div className="flex flex-none flex-col gap-[14px] 2xl:flex-row-reverse 2xl:items-end">
      <div className="relative z-[2] rounded-[14px] border border-[var(--co-line)] bg-[var(--co-raised)] px-4 py-3 2xl:-mt-[15px] 2xl:max-w-[62%] 2xl:flex-none 2xl:rounded-t-none 2xl:border-t-0 2xl:pb-4 2xl:pt-4"
        style={{ boxShadow: 'var(--co-shadow-card)' }}
      >
        <span
          aria-hidden="true"
          className="pointer-events-none absolute -left-[15px] top-0 hidden h-[15px] w-[15px] 2xl:block"
          style={{
            background:
              'radial-gradient(circle at 0 100%, transparent 14px, var(--co-line) 14px, var(--co-line) 15px, var(--co-raised) 15px)',
          }}
        />
        <div className="flex flex-wrap items-center gap-[10px] 2xl:justify-end">
          <TeamFilterPicker value={teamFilter} onChange={setTeamFilter} />
          <span className="co-mono text-[9.5px] font-semibold uppercase tracking-[.16em] text-[var(--co-ink-4)]">
            Period
          </span>
          <SingleDatePicker
            inline
            label="Start"
            value={startDate}
            maxDate={endDate}
            onChange={(start) => setStartDate(start)}
          />
          <SingleDatePicker
            inline
            align="right"
            label="End"
            value={endDate}
            minDate={startDate}
            maxDate={todayStr}
            onChange={(end) => setEndDate(end)}
          />
          {/* The span, stated. A range is two dates; how MUCH period they
              cover is what decides whether a figure means anything. */}
          <div
            className="flex h-[34px] flex-none items-center gap-[9px] rounded-[10px] border border-[var(--co-line)] bg-[var(--co-sunken)] px-3"
            title="Every figure below is evaluated over this window."
          >
            <span className="co-mono text-[9px] font-semibold uppercase tracking-[.14em] text-[var(--co-ink-3)]">
              Span
            </span>
            <span className="co-mono text-[12.5px] tabular-nums text-[var(--co-ink)]">{spanLabel}</span>
          </div>
        </div>
      </div>

      {/* AI Query Bar — full size, beside the Period tab on wide screens and
          bottom-aligned with it (same geometry as the Dashboard). */}
      <NLQueryBar
        onNavigateToReport={(reportType) => setSelectedReport(reportType)}
        variant="machine"
        className="no-print -mb-[4px] max-w-2xl flex-none 2xl:min-w-0 2xl:max-w-none 2xl:flex-1"
      />
      </div>

      {/* Scrollable report body — everything except the fixed header scrolls
          here; the MainLayout top bar and this page header stay in place. */}
      <div className="-mx-4 flex min-h-0 flex-1 flex-col gap-[14px] overflow-y-auto px-4 pb-1">
      {/* Scheduler-generated report snapshots (opened from the header button) */}
      <GeneratedReportsModal
        isOpen={showGeneratedReports}
        onClose={() => setShowGeneratedReports(false)}
      />

      {/* Report-type card — full width so every report tab has room. */}
      <div className={`flex-none ${CARD_CLASS} px-5 py-[14px]`} style={CARD_STYLE}>
        <div className="flex flex-col">
          {/* Report Type Tabs: one line (wrapping pushed the charts below the
              fold), with a fade and an arrow at whichever edge has more. */}
          <ScrollCue>
            {reportTabs.map((type) => (
              <button
                key={type.id}
                onClick={() => setSelectedReport(type.id)}
                className={`flex shrink-0 items-center gap-2 whitespace-nowrap rounded-lg border px-3.5 py-2 text-[15px] transition-colors ${
                  selectedReport === type.id
                    ? 'border-[var(--co-brass-line)] bg-[var(--co-selected)] font-semibold text-[var(--co-brass-ink)]'
                    : 'border-transparent text-[var(--co-ink-2)] hover:bg-[var(--co-sunken)]'
                }`}
              >
                <type.icon className="h-[17px] w-[17px]" />
                {type.label}
              </button>
            ))}
          </ScrollCue>
        </div>
      </div>

      {/* Overview Metrics */}
      {selectedReport === 'overview' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              title="Total Tickets"
              value={loading ? '-' : totalTickets.toLocaleString()}
              icon={Ticket}
              loading={loading}
            />
            <MetricCard
              title="Resolved"
              value={loading ? '-' : completedTickets.toLocaleString()}
              icon={CheckCircle}
              iconColor="text-green-500"
              loading={loading}
            />
            <MetricCard
              title="Avg Response Time"
              value={loading ? '-' : avgResponseTime}
              icon={Clock}
              iconColor="text-blue-500"
              loading={loading}
            />
            <MetricCard
              title="SLA Compliance"
              value={loading ? '-' : `${slaCompliance.toFixed(1)}%`}
              icon={AlertTriangle}
              iconColor="text-amber-500"
              loading={loading}
            />
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Ticket Volume */}
            <ChartCard title="Ticket Volume" description="Tickets created vs. resolved per day over the selected date range." loading={trendsLoading}>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={ticketTrends}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                  <XAxis dataKey="date" stroke={chart.axis} fontSize={12} />
                  <YAxis stroke={chart.axis} fontSize={12} />
                  <Tooltip />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="created"
                    name="Created"
                    stackId="1"
                    stroke="#3B82F6"
                    fill="#93C5FD"
                  />
                  <Area
                    type="monotone"
                    dataKey="resolved"
                    name="Resolved"
                    stackId="2"
                    stroke="#10B981"
                    fill="#6EE7B7"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* Status Distribution */}
            <ChartCard title="Status Distribution — click a slice to drill down" description="Share of tickets by current status (New, In Progress, Resolved, Pending)." loading={loading}>
              <ResponsiveContainer width="100%" height={300}>
                <RechartsPieChart>
                  <Pie
                    data={statusDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    label={makePieLabelRenderer()}
                    labelLine={false}
                    className="cursor-pointer"
                    onClick={(_: any, index: number) => {
                      const entry = statusDistribution[index];
                      if (entry) drillDown({ status: statusLabelToFilterValue(entry.name) });
                    }}
                  >
                    {statusDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </RechartsPieChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

        </>
      )}

      {/* Team Performance — every team for the period, not narrowed by the Team filter */}
      {selectedReport === 'teams' && (
        <TeamPerformanceTable rows={teamPerformance ?? []} isLoading={teamPerformanceLoading} />
      )}

      {/* Agent Performance */}
      {selectedReport === 'agents' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <MetricCard
              title="Active Agents"
              // "Active" = handled at least one ticket in the range. The roster now
              // includes every non-deleted org member (incl. zero-ticket admins/
              // supervisors), so counting the full list overstates active agents.
              // Same predicate the Avg Tickets/Agent card uses below.
              value={agentsLoading ? '-' : (agentData || []).filter(a => a.ticketsAssigned > 0).length}
              // Say what the number is against: the table below lists the whole
              // roster, so a bare "1" above two rows read as a miscount.
              description={
                agentData && agentData.length > 0
                  ? `of ${agentData.length} listed · handled a ticket in this period`
                  : undefined
              }
              icon={Users}
              loading={agentsLoading}
            />
            <MetricCard
              title="Total Tickets Handled"
              value={agentsLoading ? '-' : (
                agentData ? agentData.reduce((sum, a) => sum + a.ticketsAssigned, 0) : 0
              )}
              icon={Ticket}
              loading={agentsLoading}
            />
            <MetricCard
              title="Avg Tickets/Agent"
              value={agentsLoading ? '-' : (() => {
                // Average only over members who actually handled tickets. Dividing
                // by every roster member (which now includes zero-ticket admins/
                // supervisors) would understate real per-agent load.
                const active = (agentData || []).filter(a => a.ticketsAssigned > 0);
                return active.length > 0
                  ? Math.round(active.reduce((sum, a) => sum + a.ticketsAssigned, 0) / active.length)
                  : 0;
              })()}
              icon={Ticket}
              loading={agentsLoading}
            />
            <MetricCard
              title="Avg Satisfaction"
              value={agentsLoading ? '-' : (() => {
                // Average only over agents who actually have a CSAT score —
                // counting agents with no surveys as 0 would drag it down.
                const rated = (agentData || []).filter(a => (a.customerSatisfaction || 0) > 0);
                return rated.length > 0
                  ? (rated.reduce((sum, a) => sum + (a.customerSatisfaction || 0), 0) / rated.length).toFixed(1)
                  : '-';
              })()}
              icon={CheckCircle}
              iconColor="text-green-500"
              loading={agentsLoading}
            />
          </div>

          <ChartCard title="Agent Performance Comparison" description="Tickets assigned vs. resolved for each agent." loading={agentsLoading}>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={agentChartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                <XAxis type="number" stroke={chart.axis} fontSize={12} />
                <YAxis dataKey="name" type="category" stroke={chart.axis} fontSize={12} width={100} />
                <Tooltip />
                <Legend />
                <Bar dataKey="ticketsAssigned" name="Tickets Assigned" fill="#3B82F6" radius={[0, 4, 4, 0]} />
                <Bar dataKey="ticketsResolved" name="Tickets Resolved" fill="#10B981" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* Agent Table */}
          <div className="card overflow-hidden bg-white dark:bg-gray-800">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <h3 className="font-semibold text-gray-900 dark:text-white">Agent Details</h3>
            </div>
            {agentsLoading ? (
              <div className="p-8 flex items-center justify-center">
                <RefreshCw className="h-6 w-6 text-gray-400 animate-spin dark:text-gray-500" />
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Agent</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Assigned</th>
                    <th title="As assignee or secondary agent" className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Worked On</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Resolved</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Avg Response</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Avg Resolution</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">SLA %</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Satisfaction</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {agentPerformance.length > 0 ? agentPerformance.map((agent, idx) => (
                    <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{agent.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{agent.ticketsAssigned}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{agent.ticketsWorkedOn}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{agent.ticketsResolved}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{agent.avgResponseTime == null ? '–' : analyticsService.formatDuration(agent.avgResponseTime)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{agent.avgResolutionTime == null ? '–' : analyticsService.formatDuration(agent.avgResolutionTime)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">{agent.slaCompliance}%</td>
                      <td className="px-4 py-3">
                        <Badge
                          variant={agent.satisfaction >= 4.5 ? 'success' : agent.satisfaction >= 4.0 ? 'warning' : 'error'}
                          className={agent.satisfaction > 0 ? undefined : 'dark:bg-red-900/30 dark:text-red-300'}
                        >
                          {agent.satisfaction > 0 ? agent.satisfaction.toFixed(1) : 'N/A'}
                        </Badge>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                        No agent data available for the selected period
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* SLA Compliance */}
      {selectedReport === 'sla' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <MetricCard
              title="Overall SLA"
              value={slaLoading ? '-' : `${overallSla}%`}
              icon={CheckCircle}
              iconColor="text-green-500"
              loading={slaLoading}
            />
            <MetricCard
              title="First Response SLA"
              value={slaLoading ? '-' : `${(slaData?.firstResponse.percentage || 0).toFixed(1)}%`}
              icon={Clock}
              iconColor="text-blue-500"
              loading={slaLoading}
            />
            <MetricCard
              title="Resolution SLA"
              value={slaLoading ? '-' : `${(slaData?.resolution.percentage || 0).toFixed(1)}%`}
              icon={Ticket}
              loading={slaLoading}
            />
            <MetricCard
              title="Breached Tickets"
              value={slaLoading ? '-' : totalBreached}
              icon={AlertTriangle}
              iconColor="text-red-500"
              loading={slaLoading}
            />
          </div>

          <ChartCard title="SLA Compliance by Type" description="Percentage of tickets meeting vs. breaching SLA, split by first response and resolution." loading={slaLoading}>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={slaChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                <XAxis dataKey="name" stroke={chart.axis} fontSize={12} />
                <YAxis stroke={chart.axis} fontSize={12} domain={[0, 100]} />
                <Tooltip formatter={(value: any) => `${Number(value).toFixed(2)}%`} />
                <Legend />
                <Bar dataKey="metSLA" name="Met SLA %" fill="#10B981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="breached" name="Breached %" fill="#EF4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* SLA by Priority */}
          {slaData && Object.keys(slaData.byPriority).length > 0 && (
            <ChartCard title="SLA Compliance by Priority — click to drill down" description="SLA compliance rate for each ticket priority level.">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={Object.entries(slaData.byPriority).map(([priority, data]) => ({
                    priority: priority.charAt(0).toUpperCase() + priority.slice(1),
                    compliant: data.compliant,
                    breached: data.breached,
                    rate: data.compliant + data.breached > 0
                      ? Math.round((data.compliant / (data.compliant + data.breached)) * 100)
                      : 0,
                  }))}
                  className="cursor-pointer"
                  onClick={(data: any) => {
                    if (data?.activePayload?.[0]?.payload?.priority) {
                      drillDown({ priority: data.activePayload[0].payload.priority.toLowerCase() });
                    }
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                  <XAxis dataKey="priority" stroke={chart.axis} fontSize={12} />
                  <YAxis stroke={chart.axis} fontSize={12} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="compliant" name="Compliant" fill="#10B981" stackId="a" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="breached" name="Breached" fill="#EF4444" stackId="a" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          )}
        </>
      )}

      {/* Channel Analysis */}
      {selectedReport === 'channels' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <MetricCard key={i} title="Loading..." value="-" icon={Activity} loading />
              ))
            ) : channelData.length > 0 ? (
              channelData.slice(0, 4).map((channel) => (
                <MetricCard
                  key={channel.name}
                  title={channel.name}
                  value={channel.tickets}
                  icon={Activity}
                />
              ))
            ) : (
              <div className="col-span-4 text-center py-8 text-sm text-gray-500 dark:text-gray-400">
                No channel data available for the selected period
              </div>
            )}
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-400">
            Cards show all tickets <strong>created</strong> per channel in the selected date range (including backtracked).
            The chart below shows only <strong>open</strong> tickets — excluding resolved, closed, and backtracked.
          </p>

          <div className="grid grid-cols-1 gap-6">
            <ChartCard title="Open Channel Distribution" description="Share of currently open tickets by channel (email, chat, WhatsApp, etc.)." loading={loading}>
              <ResponsiveContainer width="100%" height={300}>
                <RechartsPieChart>
                  <Pie
                    data={channelOpen}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    dataKey="tickets"
                    label={makePieLabelRenderer({ separator: ': ' })}
                    labelLine={false}
                    className="cursor-pointer"
                    onClick={(_: any, index: number) => {
                      const entry = channelOpen[index];
                      if (entry) drillDown({ channel: entry.name.toLowerCase() });
                    }}
                  >
                    {channelOpen.map((_, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={CHANNEL_COLORS[index % CHANNEL_COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                </RechartsPieChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
        </>
      )}

      {/* Backtracked — tickets auto-archived after reaching the final pipeline stage */}
      {selectedReport === 'backtracked' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              title="Backtracked"
              value={loading ? '-' : backtrackedTickets.toLocaleString()}
              icon={Archive}
              iconColor="text-purple-500"
              loading={loading}
            />
            <MetricCard
              title="Total Tickets"
              value={loading ? '-' : totalTickets.toLocaleString()}
              icon={Ticket}
              loading={loading}
            />
            <MetricCard
              title="Backtracked Rate"
              value={loading ? '-' : (totalTickets > 0 ? `${Math.round((backtrackedTickets / totalTickets) * 100)}%` : '0%')}
              icon={Gauge}
              iconColor="text-amber-500"
              loading={loading}
            />
          </div>

          <div className="mt-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-purple-500">
                <Archive className="w-5 h-5" />
              </div>
              <div className="text-sm text-gray-600 dark:text-gray-300">
                <p className="font-medium text-gray-900 dark:text-gray-100">What “backtracked” means</p>
                <p className="mt-1">
                  Backtracked tickets are those that reached the final stage of their pipeline and were
                  automatically archived out of the active board at the end of the day. The count above reflects
                  tickets <strong>created within the selected date range</strong>. Backtracked tickets are excluded
                  from the Status, Priority, and Channel charts but are included in the Total Tickets count.
                </p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Ticket Analytics — Deep Dive */}
      {selectedReport === 'tickets' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <MetricCard
              title="Total Volume"
              value={loading ? '-' : totalTickets.toLocaleString()}
              icon={Ticket}
              loading={loading}
            />
            <MetricCard
              title="Resolution Rate"
              value={loading ? '-' : (totalTickets > 0 ? `${Math.round((resolvedTickets / totalTickets) * 100)}%` : '0%')}
              icon={TrendingUp}
              iconColor="text-green-500"
              loading={loading}
            />
            <MetricCard
              title="Avg Resolution Time"
              value={loading ? '-' : avgResolutionTime}
              icon={Clock}
              iconColor="text-blue-500"
              loading={loading}
            />
            <MetricCard
              title="Backlog"
              value={loading ? '-' : ((metrics?.tickets.new || 0) + (metrics?.tickets.in_progress || 0) + (metrics?.tickets.pending || 0))}
              icon={AlertTriangle}
              iconColor="text-amber-500"
              loading={loading}
            />
          </div>

          {/* Lifecycle + Priority Breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartCard title="Ticket Lifecycle — click a bar to drill down" description="Number of tickets in each status (New, In Progress, Pending, Resolved)." loading={loading}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart layout="vertical" data={[
                  { status: 'New', count: metrics?.tickets.new || 0 },
                  { status: 'In Progress', count: metrics?.tickets.in_progress || 0 },
                  { status: 'Pending', count: metrics?.tickets.pending || 0 },
                  { status: 'Resolved', count: metrics?.tickets.resolved || 0 },
                ]}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                  <XAxis type="number" stroke={chart.axis} fontSize={12} />
                  <YAxis dataKey="status" type="category" stroke={chart.axis} fontSize={12} width={80} />
                  <Tooltip />
                  <Bar
                    dataKey="count"
                    name="Tickets"
                    radius={[0, 4, 4, 0]}
                    className="cursor-pointer"
                    onClick={(data: any) => {
                      if (data?.status) drillDown({ status: statusLabelToFilterValue(data.status) });
                    }}
                  >
                    <Cell fill={STATUS_COLORS.new} />
                    <Cell fill={STATUS_COLORS.in_progress} />
                    <Cell fill={STATUS_COLORS.pending} />
                    <Cell fill={STATUS_COLORS.resolved} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Priority Breakdown — click a slice to drill down" description="Share of tickets by priority (low, medium, high, urgent)." loading={loading}>
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <ResponsiveContainer width="100%" height={300} className="sm:!w-1/2">
                  <RechartsPieChart>
                    <Pie
                      data={priorityDistribution}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      className="cursor-pointer"
                      onClick={(_: any, index: number) => {
                        const entry = priorityDistribution[index];
                        if (entry) drillDown({ priority: entry.name.toLowerCase() });
                      }}
                    >
                      {priorityDistribution.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </RechartsPieChart>
                </ResponsiveContainer>
                <div className="w-full sm:w-1/2 space-y-2">
                  {priorityDistribution.map((p, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => drillDown({ priority: p.name.toLowerCase() })}
                      className="w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-left cursor-pointer"
                    >
                      <span className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-white">
                        <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: p.color }} />
                        {p.name}
                      </span>
                      <span className="text-sm text-gray-600 dark:text-gray-300 tabular-nums">
                        {p.value} <span className="text-gray-400">({prioritySum > 0 ? Math.round((p.value / prioritySum) * 100) : 0}%)</span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </ChartCard>
          </div>

          {/* Assignment — assigned vs unassigned tickets created in range */}
          <ChartCard title="Assignment — assigned vs unassigned" description="Tickets that have an assignee vs. those still unassigned." loading={loading}>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <ResponsiveContainer width="100%" height={300} className="sm:!w-1/2">
                <RechartsPieChart>
                  <Pie
                    data={assignmentDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {assignmentDistribution.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </RechartsPieChart>
              </ResponsiveContainer>
              <div className="w-full sm:w-1/2 space-y-2">
                {assignmentDistribution.map((a, idx) => (
                  <div
                    key={idx}
                    className="w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg"
                  >
                    <span className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-white">
                      <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: a.color }} />
                      {a.name}
                    </span>
                    <span className="text-sm text-gray-600 dark:text-gray-300 tabular-nums">
                      {a.value.toLocaleString()} <span className="text-gray-400">({assignmentTotal > 0 ? Math.round((a.value / assignmentTotal) * 100) : 0}%)</span>
                    </span>
                  </div>
                ))}
                <div className="flex items-center justify-between gap-3 px-3 py-2 border-t border-gray-100 dark:border-gray-700 mt-1 pt-3">
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">Total</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white tabular-nums">{assignmentTotal.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between gap-3 px-3 text-xs text-gray-500 dark:text-gray-400">
                  <span>Assigned : Unassigned ratio</span>
                  <span className="tabular-nums">
                    {unassignedTickets === 0
                      ? (assignedTickets > 0 ? `${assignedTickets.toLocaleString()} : 0` : '–')
                      : `${(assignedTickets / unassignedTickets).toFixed(2)} : 1`}
                  </span>
                </div>
              </div>
            </div>
          </ChartCard>

          {/* Ticket Trends */}
          <ChartCard title="Ticket Trends" description="Tickets created vs. resolved over time." loading={trendsLoading}>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={ticketTrends}>
                <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                <XAxis dataKey="date" stroke={chart.axis} fontSize={12} />
                <YAxis stroke={chart.axis} fontSize={12} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="created" name="Created" stroke="#3B82F6" strokeWidth={2} />
                <Line type="monotone" dataKey="resolved" name="Resolved" stroke="#10B981" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

        </>
      )}

      {/* CSAT / NPS */}
      {selectedReport === 'csat' && (
        <>
          {csatLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 text-gray-400 animate-spin dark:text-gray-500" />
            </div>
          ) : csatStats ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <MetricCard title="Avg Rating" value={csatStats.avgRating.toFixed(1)} icon={Star} iconColor="text-yellow-500" description="Average customer satisfaction rating (1–5 stars) across completed surveys." />
                <MetricCard title="NPS Score" value={csatStats.nps.toFixed(1)} icon={TrendingUp} iconColor="text-blue-500" description="Net Promoter Score (−100 to 100): % promoters minus % detractors." />
                <MetricCard title="Response Rate" value={`${csatStats.responseRate.toFixed(1)}%`} icon={CheckCircle} iconColor="text-green-500" description="Share of sent surveys that received a response." />
                <MetricCard title="Total Surveys" value={csatStats.total} icon={BarChart3} iconColor="text-purple-500" description="Total surveys sent in the selected date range." />
              </div>

              {/* Survey Status */}
              <div className="card bg-white dark:bg-gray-800">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="font-semibold text-gray-900 dark:text-white">Survey Status</h3>
                </div>
                <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <p className="text-2xl font-bold text-green-600">{csatStats.completed}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Completed</p>
                    <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">Surveys the customer answered.</p>
                  </div>
                  <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                    <p className="text-2xl font-bold text-yellow-600">{csatStats.pending}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Pending</p>
                    <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">Sent surveys still awaiting a response.</p>
                  </div>
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                    <p className="text-2xl font-bold text-red-600">{csatStats.expired}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Expired</p>
                    <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">Surveys that closed with no response.</p>
                  </div>
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <p className="text-2xl font-bold text-blue-600">{csatStats.total}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Total</p>
                    <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">All surveys sent in the selected range.</p>
                  </div>
                </div>
              </div>

              {/* Rating Distribution */}
              <ChartCard title="Rating Distribution" description="Count of customer satisfaction responses by star rating.">
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={[5, 4, 3, 2, 1].map(r => ({
                    rating: `${r}★`,
                    count: csatStats.ratingDistribution[r] || 0,
                  }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                    <XAxis dataKey="rating" stroke={chart.axis} fontSize={12} />
                    <YAxis stroke={chart.axis} fontSize={12} />
                    <Tooltip />
                    <Bar dataKey="count" name="Responses" radius={[4, 4, 0, 0]}>
                      {[5, 4, 3, 2, 1].map((r, i) => (
                        <Cell key={i} fill={r >= 4 ? '#10B981' : r === 3 ? '#F59E0B' : '#EF4444'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </>
          ) : (
            <div className="card p-12 text-center bg-white dark:bg-gray-800">
              <Star className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">No CSAT data</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                CSAT data will appear once satisfaction surveys are sent and responded to
              </p>
            </div>
          )}
        </>
      )}

      {/* AI Chatbot */}
      {selectedReport === 'ai' && (
        <>
          {aiLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 text-gray-400 animate-spin dark:text-gray-500" />
            </div>
          ) : aiAnalytics ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <MetricCard title="Total Conversations" value={aiAnalytics.total_conversations} icon={Bot} iconColor="text-indigo-500" />
                <MetricCard title="Resolution Rate" value={`${(aiAnalytics.resolution_rate * 100).toFixed(1)}%`} icon={CheckCircle} iconColor="text-green-500" />
                <MetricCard title="Handoff Rate" value={`${(aiAnalytics.handoff_rate * 100).toFixed(1)}%`} icon={Users} iconColor="text-yellow-500" />
                <MetricCard title="Avg Confidence" value={`${(aiAnalytics.avg_confidence * 100).toFixed(1)}%`} icon={Activity} iconColor="text-purple-500" />
              </div>

              {/* Conversation Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="card p-6 bg-white dark:bg-gray-800">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">Conversation Outcomes</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Auto-Resolved</span>
                      <span className="text-sm font-medium text-green-600">{aiAnalytics.total_resolutions}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Handed Off</span>
                      <span className="text-sm font-medium text-yellow-600">{aiAnalytics.total_handoffs}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Total Messages</span>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{aiAnalytics.total_messages}</span>
                    </div>
                  </div>
                </div>

                <div className="card p-6 bg-white dark:bg-gray-800">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">Avg Response Time</h3>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white">
                    {aiAnalytics.avg_response_time_ms < 1000
                      ? `${Math.round(aiAnalytics.avg_response_time_ms)}ms`
                      : `${(aiAnalytics.avg_response_time_ms / 1000).toFixed(1)}s`}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">AI response latency</p>
                </div>

                <div className="card p-6 bg-white dark:bg-gray-800">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">Avg Sentiment</h3>
                  <div className="flex items-center gap-2">
                    <p className="text-3xl font-bold text-gray-900 dark:text-white">
                      {(aiAnalytics.avg_sentiment * 100).toFixed(0)}%
                    </p>
                    <span className={`text-sm ${aiAnalytics.avg_sentiment > 0.5 ? 'text-green-600' : 'text-yellow-600'}`}>
                      {aiAnalytics.avg_sentiment > 0.7 ? 'Positive' : aiAnalytics.avg_sentiment > 0.4 ? 'Neutral' : 'Negative'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Intent Distribution */}
              {Object.keys(aiAnalytics.intent_distribution).length > 0 && (
                <ChartCard title="Top Intents" description="Most frequent customer intents detected by AI.">
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={Object.entries(aiAnalytics.intent_distribution)
                        .sort(([, a], [, b]) => b - a)
                        .slice(0, 10)
                        .map(([intent, count]) => ({
                          intent: intent.replace(/_/g, ' '),
                          count,
                        }))}
                      layout="vertical"
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
                      <XAxis type="number" stroke={chart.axis} fontSize={12} />
                      <YAxis dataKey="intent" type="category" stroke={chart.axis} fontSize={12} width={140} />
                      <Tooltip />
                      <Bar dataKey="count" name="Count" fill="#6366F1" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              )}

              {/* Handoff Reasons */}
              {Object.keys(aiAnalytics.handoff_reasons).length > 0 && (
                <div className="card bg-white dark:bg-gray-800">
                  <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="font-semibold text-gray-900 dark:text-white">Top Handoff Reasons</h3>
                  </div>
                  <div className="p-4 grid grid-cols-2 md:grid-cols-3 gap-3">
                    {Object.entries(aiAnalytics.handoff_reasons)
                      .sort(([, a], [, b]) => b - a)
                      .map(([reason, count]) => (
                        <div key={reason} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                          <p className="text-lg font-bold text-gray-900 dark:text-white">{count}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{reason.replace(/_/g, ' ')}</p>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="card p-12 text-center bg-white dark:bg-gray-800">
              <Bot className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">No AI chatbot data</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                AI analytics will appear once conversations are handled by the chatbot
              </p>
            </div>
          )}
        </>
      )}

      {/* Live Chat */}
      {selectedReport === 'chat' && (
        <>
          {chatLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 text-gray-400 animate-spin dark:text-gray-500" />
            </div>
          ) : chatStats ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <MetricCard title="Waiting" value={chatStats.waiting} icon={Clock} iconColor="text-yellow-500" />
                <MetricCard title="Active Sessions" value={chatStats.active} icon={MessageCircle} iconColor="text-green-500" />
                <MetricCard title="Ended Today" value={chatStats.endedToday} icon={CheckCircle} iconColor="text-blue-500" />
              </div>

              {/* Session Overview */}
              <div className="card bg-white dark:bg-gray-800">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="font-semibold text-gray-900 dark:text-white">Session Overview</h3>
                </div>
                <div className="p-4 space-y-4">
                  {(() => {
                    const total = chatStats.waiting + chatStats.active + chatStats.endedToday;
                    if (total === 0) return <p className="text-sm text-gray-500 dark:text-gray-400">No sessions today</p>;
                    return (
                      <>
                        <div className="flex h-6 rounded-full overflow-hidden">
                          {chatStats.active > 0 && (
                            <div
                              className="bg-green-500 flex items-center justify-center text-xs text-white font-medium"
                              style={{ width: `${(chatStats.active / total) * 100}%` }}
                            >
                              {chatStats.active}
                            </div>
                          )}
                          {chatStats.waiting > 0 && (
                            <div
                              className="bg-yellow-500 flex items-center justify-center text-xs text-white font-medium"
                              style={{ width: `${(chatStats.waiting / total) * 100}%` }}
                            >
                              {chatStats.waiting}
                            </div>
                          )}
                          {chatStats.endedToday > 0 && (
                            <div
                              className="bg-blue-500 flex items-center justify-center text-xs text-white font-medium"
                              style={{ width: `${(chatStats.endedToday / total) * 100}%` }}
                            >
                              {chatStats.endedToday}
                            </div>
                          )}
                        </div>
                        <div className="flex gap-4 text-xs">
                          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-500 rounded-full" /> Active</span>
                          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-yellow-500 rounded-full" /> Waiting</span>
                          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-blue-500 rounded-full" /> Ended</span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Chat Distribution Pie */}
              <ChartCard title="Chat Distribution" description="Live chat sessions by state (active, waiting, ended today).">
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsPieChart>
                    <Pie
                      data={[
                        { name: 'Active', value: chatStats.active, color: '#10B981' },
                        { name: 'Waiting', value: chatStats.waiting, color: '#F59E0B' },
                        { name: 'Ended', value: chatStats.endedToday, color: '#3B82F6' },
                      ].filter(d => d.value > 0)}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      label={makePieLabelRenderer()}
                      labelLine={false}
                    >
                      {[
                        { color: '#10B981' },
                        { color: '#F59E0B' },
                        { color: '#3B82F6' },
                      ].map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </RechartsPieChart>
                </ResponsiveContainer>
              </ChartCard>
            </>
          ) : (
            <div className="card p-12 text-center bg-white dark:bg-gray-800">
              <MessageCircle className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">No live chat data</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Live chat analytics will appear once chat sessions are initiated
              </p>
            </div>
          )}
        </>
      )}

      {/* Automations */}
      {selectedReport === 'automations' && (
        <>
          {rulesLoading && execsLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 text-gray-400 animate-spin dark:text-gray-500" />
            </div>
          ) : automationRules || automationExecs ? (
            <>
              {automationRules && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <MetricCard title="Total Rules" value={automationRules.total} icon={Zap} iconColor="text-purple-500" />
                  <MetricCard title="Active Rules" value={automationRules.active} icon={Activity} iconColor="text-green-500" />
                </div>
              )}

              {automationExecs && (
                <>
                  <div className="card bg-white dark:bg-gray-800">
                    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                      <h3 className="font-semibold text-gray-900 dark:text-white">Executions Today</h3>
                    </div>
                    <div className="p-4 grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
                      <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">{automationExecs.total}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Total</p>
                      </div>
                      <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                        <p className="text-2xl font-bold text-green-600">{automationExecs.completed}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Completed</p>
                      </div>
                      <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                        <p className="text-2xl font-bold text-red-600">{automationExecs.failed}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Failed</p>
                      </div>
                      <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                        <p className="text-2xl font-bold text-yellow-600">{automationExecs.pending}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Pending</p>
                      </div>
                      <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <p className="text-2xl font-bold text-blue-600">{automationExecs.running}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Running</p>
                      </div>
                    </div>
                  </div>

                  {/* Success Rate */}
                  <ChartCard title="Execution Results" description="Automation runs by outcome (completed, failed, pending, running).">
                    <ResponsiveContainer width="100%" height={300}>
                      <RechartsPieChart>
                        <Pie
                          data={[
                            { name: 'Completed', value: automationExecs.completed, color: '#10B981' },
                            { name: 'Failed', value: automationExecs.failed, color: '#EF4444' },
                            { name: 'Pending', value: automationExecs.pending, color: '#F59E0B' },
                            { name: 'Running', value: automationExecs.running, color: '#3B82F6' },
                          ].filter(d => d.value > 0)}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey="value"
                          label={makePieLabelRenderer()}
                          labelLine={false}
                        >
                          {[
                            { color: '#10B981' },
                            { color: '#EF4444' },
                            { color: '#F59E0B' },
                            { color: '#3B82F6' },
                          ].map((entry, i) => (
                            <Cell key={i} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </RechartsPieChart>
                    </ResponsiveContainer>
                  </ChartCard>
                </>
              )}
            </>
          ) : (
            <div className="card p-12 text-center bg-white dark:bg-gray-800">
              <Zap className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">No automation data</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Automation analytics will appear once rules are created and executed
              </p>
            </div>
          )}
        </>
      )}

      {/* Pipeline Analysis */}
      {selectedReport === 'pipelines' && (
        <>
          {pipelinesLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 text-gray-400 animate-spin dark:text-gray-500" />
            </div>
          ) : pipelines && pipelines.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <MetricCard title="Total Pipelines" value={pipelines.length} icon={GitBranch} iconColor="text-indigo-500" />
                <MetricCard
                  title="Total Stages"
                  value={pipelines.reduce((sum: number, p: any) => sum + (p.stages?.length || 0), 0)}
                  icon={Activity}
                  iconColor="text-purple-500"
                />
                <MetricCard
                  title="Avg Stages/Pipeline"
                  value={Math.round(
                    pipelines.reduce((sum: number, p: any) => sum + (p.stages?.length || 0), 0) / pipelines.length,
                  )}
                  icon={BarChart3}
                  iconColor="text-blue-500"
                />
              </div>

              {/* Pipeline stage breakdown */}
              {pipelines.map((pipeline: any) => (
                <ChartCard key={pipeline.id} title={`Pipeline: ${pipeline.name}`} description="Stages in this pipeline, shown in order.">
                  {pipeline.stages && pipeline.stages.length > 0 ? (
                    <div className="flex flex-wrap items-center gap-2 py-4">
                      {[...pipeline.stages]
                        .sort((a: any, b: any) => a.sortOrder - b.sortOrder)
                        .map((stage: any, index: number, arr: any[]) => {
                          const stageColor = stage.color || '#6B7280';
                          return (
                            <div key={stage.id} className="flex items-center gap-2">
                              <span
                                className="inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium backdrop-blur-sm shadow-sm"
                                style={{
                                  backgroundColor: `${stageColor}14`,
                                  borderColor: `${stageColor}59`,
                                  color: stageColor,
                                }}
                              >
                                <span
                                  className="w-2 h-2 rounded-full flex-shrink-0 ring-2 ring-white dark:ring-gray-800"
                                  style={{ backgroundColor: stageColor }}
                                />
                                {stage.name}
                              </span>
                              {index < arr.length - 1 && (
                                <span className="text-gray-400 dark:text-gray-500" aria-hidden="true">
                                  &rarr;
                                </span>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
                      No stages configured
                    </p>
                  )}
                </ChartCard>
              ))}
            </>
          ) : (
            <div className="card p-12 text-center bg-white dark:bg-gray-800">
              <GitBranch className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">No pipeline data</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Pipeline analytics will appear once pipelines are configured in settings
              </p>
            </div>
          )}
        </>
      )}

      {/* Custom Metrics — overview/landing (summary + links to each metric tab) */}
      {selectedReport === 'custom-metrics' && (
        <>{renderCustomMetricsOverview()}</>
      )}

      {/* One tab per custom metric — renders just that metric's card */}
      {activeCustomMetricId !== null && (
        <>{activeCustomMetric ? renderMetricCard(activeCustomMetric) : renderCustomMetricsOverview()}</>
      )}
      </div>
    </div>
  );
}

/** Inline card that evaluates a custom metric and renders it as the chart the
 *  metric is configured for (bar / line / pie / gauge / number). */
function CustomMetricReportCard({ metric, dateRange }: { metric: any; dateRange: DateRange }) {
  const chartType: string = metric?.chartType || 'number';
  const displayFormat: string | undefined = metric?.displayFormat;
  const unit: string | undefined = metric?.unit;
  const chart = useThemeColors({ grid: '--co-line', axis: '--co-ink-3' });
  const { data: result, isLoading } = useEvaluateMetric(metric.id, dateRange.start, dateRange.end);

  // Bucketing granularity from the selected range (mirrors the Ticket Volume trend).
  const spanDays = Math.max(
    0,
    Math.round((new Date(dateRange.end).getTime() - new Date(dateRange.start).getTime()) / 86_400_000),
  );
  const interval = spanDays <= 31 ? 'day' : spanDays <= 180 ? 'week' : 'month';
  // Trend series (value over time) — only meaningful for bar/line charts.
  const wantsTrend = chartType === 'bar' || chartType === 'line';
  const { data: series } = useMetricTimeSeries(metric.id, dateRange.start, dateRange.end, interval);
  const trend: { period: string; value: number }[] =
    wantsTrend && Array.isArray(series)
      ? series.map((p: any) => ({ period: (p?.period ?? p?.date ?? '').toString(), value: Number(p?.value) || 0 }))
      : [];

  if (isLoading) {
    return <div className="h-[260px] bg-gray-100 dark:bg-gray-700 rounded animate-pulse" />;
  }
  if (!result) {
    return <p className="text-sm text-gray-400 dark:text-gray-500">No data for the selected range</p>;
  }

  // Comparison metrics → multi-series bar (or pie) of the compared counts.
  if (result.type === 'comparison' && Array.isArray(result.metrics)) {
    const data = result.metrics.map((m: { label: string; value: number }) => ({ name: m.label, value: m.value }));
    return (
      <div>
        <ResponsiveContainer width="100%" height={260}>
          {chartType === 'pie' ? (
            <RechartsPieChart>
              <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={95} label>
                {data.map((d: { name: string }, i: number) => (
                  <Cell key={`${d.name}-${i}`} fill={METRIC_CHART_COLORS[i % METRIC_CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </RechartsPieChart>
          ) : (
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
              <XAxis dataKey="name" stroke={chart.axis} fontSize={12} />
              <YAxis stroke={chart.axis} fontSize={12} />
              <Tooltip />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {data.map((d: { name: string }, i: number) => (
                  <Cell key={`${d.name}-${i}`} fill={METRIC_CHART_COLORS[i % METRIC_CHART_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          )}
        </ResponsiveContainer>
        {result.successRate != null && (
          <p className="text-xs text-gray-500 mt-2 dark:text-gray-400">
            Success Rate: <span className="font-semibold">{result.successRate}%</span> &middot; Total: {result.total}
          </p>
        )}
      </div>
    );
  }

  // Single scalar value.
  const value = typeof result.value === 'number' ? result.value : Number(result.value) || 0;
  const label = getMetricTabLabel(metric);
  const single = [{ name: label, value }];
  const valueText = formatMetricValue(value, displayFormat, unit);

  // Trend over time (bar/line) when the series has data points — the real chart.
  if (wantsTrend && trend.length > 0) {
    return (
      <div>
        <ResponsiveContainer width="100%" height={260}>
          {chartType === 'line' ? (
            <LineChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
              <XAxis dataKey="period" stroke={chart.axis} fontSize={12} />
              <YAxis stroke={chart.axis} fontSize={12} />
              <Tooltip />
              <Line type="monotone" dataKey="value" name={label} stroke="#3B82F6" strokeWidth={2} />
            </LineChart>
          ) : (
            <BarChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
              <XAxis dataKey="period" stroke={chart.axis} fontSize={12} />
              <YAxis stroke={chart.axis} fontSize={12} />
              <Tooltip />
              <Bar dataKey="value" name={label} fill="#3B82F6" radius={[4, 4, 0, 0]} />
            </BarChart>
          )}
        </ResponsiveContainer>
        <p className="text-center text-xs text-gray-500 mt-1 dark:text-gray-400">
          Total: <span className="font-semibold text-gray-900 dark:text-white">{valueText}</span>
        </p>
      </div>
    );
  }

  // Explicit "Number" card (also the safe default for unknown types).
  if (chartType === 'number') {
    return (
      <div>
        <p className="text-3xl font-bold text-gray-900 dark:text-white">{valueText}</p>
        <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">{toTitleCase(result.type)}</p>
      </div>
    );
  }

  // Pie / gauge of a single value — for a percentage, show value vs the rest of 100.
  if (chartType === 'pie' || chartType === 'gauge') {
    const pieData =
      displayFormat === 'percentage'
        ? [{ name: label, value }, { name: 'Remaining', value: Math.max(0, 100 - value) }]
        : single;
    return (
      <div>
        <ResponsiveContainer width="100%" height={260}>
          <RechartsPieChart>
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={chartType === 'gauge' ? 70 : 0}
              outerRadius={95}
            >
              {pieData.map((d: { name: string }, i: number) => (
                <Cell key={`${d.name}-${i}`} fill={i === 0 ? METRIC_CHART_COLORS[0] : '#E5E7EB'} />
              ))}
            </Pie>
            <Tooltip />
          </RechartsPieChart>
        </ResponsiveContainer>
        <p className="text-center text-sm font-semibold text-gray-900 dark:text-white">{valueText}</p>
      </div>
    );
  }

  // Bar / line of a single value (one data point).
  return (
    <div>
      <ResponsiveContainer width="100%" height={260}>
        {chartType === 'line' ? (
          <LineChart data={single}>
            <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
            <XAxis dataKey="name" stroke={chart.axis} fontSize={12} />
            <YAxis stroke={chart.axis} fontSize={12} />
            <Tooltip />
            <Line type="monotone" dataKey="value" stroke="#3B82F6" strokeWidth={2} />
          </LineChart>
        ) : (
          <BarChart data={single}>
            <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
            <XAxis dataKey="name" stroke={chart.axis} fontSize={12} />
            <YAxis stroke={chart.axis} fontSize={12} />
            <Tooltip />
            <Bar dataKey="value" fill="#3B82F6" radius={[4, 4, 0, 0]} />
          </BarChart>
        )}
      </ResponsiveContainer>
      <p className="text-center text-sm font-semibold text-gray-900 dark:text-white mt-1">{valueText}</p>
    </div>
  );
}
